import type { StopWorkflowResult } from "@/app/_components/adminWorkflows/autodialerSessionChannel";
import type { StopWorkflowOptions } from "@/lib/modules/calls/workflow/types";

export const isWorkflowStopConflict = (error: unknown): boolean =>
  typeof error === "object" &&
  error !== null &&
  "data" in error &&
  (error as { data?: { code?: string } }).data?.code === "CONFLICT";

export const reconcileWorkflowStopConflict = async ({
  yieldOwnership,
  rehydrate,
  clearLocalWorkflow,
  resumeElection,
}: {
  yieldOwnership: () => void;
  rehydrate: () => Promise<boolean>;
  clearLocalWorkflow: () => void;
  resumeElection: () => void;
}): Promise<void> => {
  yieldOwnership();
  const rehydrated = await rehydrate();
  if (!rehydrated) {
    clearLocalWorkflow();
    return;
  }
  resumeElection();
};

/**
 * Applies the outcome of a stopWorkflow call — whether it ran directly on the
 * owner tab or came back over the relay channel from a follower's request —
 * so the toast/analytics the stop produces always surface on the tab that
 * actually asked for the stop, not wherever ownership happened to live.
 */
export const applyStopWorkflowResult = ({
  result,
  options,
  trackStoppedAction,
  trackInactivityTimeoutAction,
  showSummaryToast,
  showInfoToast,
  showErrorToast,
}: {
  result: StopWorkflowResult;
  options?: StopWorkflowOptions;
  trackStoppedAction: (
    summaryStats: NonNullable<StopWorkflowResult["summaryStats"]>,
  ) => void;
  trackInactivityTimeoutAction: (
    summaryStats: NonNullable<StopWorkflowResult["summaryStats"]>,
  ) => void;
  showSummaryToast: (
    summaryStats: NonNullable<StopWorkflowResult["summaryStats"]>,
    reason: "user_stopped" | "queue_exhausted",
  ) => void;
  showInfoToast: (message: string) => void;
  showErrorToast: (message: string) => void;
}): void => {
  if (!result.stopped) {
    if (result.errorMessage) {
      showErrorToast(result.errorMessage);
    }
    return;
  }

  const summaryStats = result.summaryStats;
  if (!summaryStats) {
    return;
  }

  if (options?.sessionEndReason === "user_stopped") {
    trackStoppedAction(summaryStats);
  } else if (options?.sessionEndReason === "inactivity_timeout") {
    trackInactivityTimeoutAction(summaryStats);
  }

  if (options?.showSessionSummary) {
    showSummaryToast(
      summaryStats,
      options.sessionEndReason === "queue_exhausted"
        ? "queue_exhausted"
        : "user_stopped",
    );
  } else if (options?.sessionEndReason === "inactivity_timeout") {
    showInfoToast("Workflow ended after 1 hour without a call.");
  }
};
